/*!
 * Portable AI Front-Desk / Customer-Service Widget
 * -------------------------------------------------
 * Drop-in chat widget for adding self-serve Q&A (and an easy path to a real
 * human) to a website, regardless of platform (WordPress, Wix, Squarespace,
 * GoDaddy, Shopify, custom HTML).
 *
 * ENGINE vs CONFIG: this file is the reusable engine. Everything specific to
 * a given site lives in a small window.NJNW_CONFIG object set BEFORE this
 * script loads. To onboard a new site, you change the config, not this file.
 *
 * Isolation: renders inside a Shadow DOM so the host site's CSS cannot break
 * the widget, and the widget's CSS cannot leak into the host site.
 *
 * Optional backend: set config.leadWebhook to a URL and captured leads will
 * be POSTed there as JSON. If unset, the widget still works fully offline —
 * it just shows the confirmation message without sending data anywhere,
 * which is fine for demos.
 *
 * Optional booking flow: set config.showBooking = false to hide the "Book an
 * appointment" quick-menu option entirely (e.g. for a storefront that has no
 * appointments to book). Defaults to true so existing installs are unaffected.
 *
 * Optional contact link: set config.contactUrl to a URL and a "Contact us"
 * quick-menu option will navigate to it (same tab) — useful as an escape
 * hatch to a real contact form once the FAQ list runs out of answers.
 *
 * Free-text matching: if the visitor types instead of clicking a quick-reply
 * button, the engine does a lightweight keyword match against config.faqs
 * (matching against each FAQ's question more heavily than its answer) and
 * replies with the best match. No match found -> a fallback message pointing
 * to config.contactUrl (if set) rather than a dead-end non-answer.
 *
 * Optional label overrides: set config.labels = { book, faq, call, contact }
 * to change any of the quick-menu button text without touching this file.
 */
(function () {
  "use strict";

  var DEFAULT_LABELS = {
    book: "Book an appointment",
    faq: "Ask a question",
    call: "Call now",
    contact: "Contact us",
  };

  var DEFAULTS = {
    businessName: "This Business",
    accentColor: "#e8642c",
    phone: "",
    greeting: "Hi! I'm the virtual assistant here. Want to book an appointment, ask a question, or call us directly?",
    faqs: [],
    leadWebhook: null,
    position: "bottom-right", // or "bottom-left"
    poweredByLabel: "AI front desk by NJ Northman Media",
    showBooking: true,
    contactUrl: null,
    labels: DEFAULT_LABELS,
  };

  var USER_CFG = window.NJNW_CONFIG || {};
  var CFG = Object.assign({}, DEFAULTS, USER_CFG);
  CFG.labels = Object.assign({}, DEFAULT_LABELS, USER_CFG.labels || {});

  // ---------- Lightweight free-text -> FAQ matching ----------
  var STOPWORDS = [
    "the", "and", "for", "are", "but", "not", "you", "your", "with", "this",
    "that", "have", "has", "had", "did", "does", "doe", "can", "will", "what",
    "how", "why", "when", "where", "who", "about", "from", "into", "out",
    "get", "got", "use", "used", "using", "just", "much", "many", "any",
    "all", "our", "was", "were", "been", "being", "which", "its", "let",
    "lets", "need", "want", "like", "there", "here", "then", "than", "off",
    "still", "some", "make", "made",
  ];

  function normalizeWords(str) {
    return String(str)
      .toLowerCase()
      .replace(/['’]/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter(function (w) {
        return w.length > 2 && STOPWORDS.indexOf(w) === -1;
      });
  }

  function findFaqMatch(input) {
    if (!CFG.faqs || !CFG.faqs.length) return null;
    var inputWords = normalizeWords(input);
    if (!inputWords.length) return null;

    var best = null;
    var bestScore = 0;
    CFG.faqs.forEach(function (f) {
      var qWords = normalizeWords(f.q);
      var aWords = normalizeWords(f.a);
      var score = 0;
      inputWords.forEach(function (w) {
        if (qWords.indexOf(w) !== -1) score += 2;
        else if (aWords.indexOf(w) !== -1) score += 1;
      });
      if (score > bestScore) {
        bestScore = score;
        best = f;
      }
    });
    // require a couple of real signal words to match before trusting it --
    // avoids confidently answering the wrong question off a single weak hit.
    return bestScore >= 2 ? best : null;
  }

  // ---------- Build host container (light DOM, positioned via inline styles) ----------
  var host = document.createElement("div");
  host.id = "njnw-widget-host";
  var side = CFG.position === "bottom-left" ? "left" : "right";
  host.style.cssText = [
    "display:block",
    "position:fixed",
    "bottom:20px",
    side + ":20px",
    "z-index:2147483000",
    "font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif",
  ].join(";");
  document.addEventListener("DOMContentLoaded", mount);
  if (document.readyState === "complete" || document.readyState === "interactive") {
    mount();
  }

  function mount() {
    if (document.getElementById("njnw-widget-host")) return; // avoid double-mount
    document.body.appendChild(host);
    var shadow = host.attachShadow({ mode: "open" });
    shadow.innerHTML = buildMarkup();
    injectStyle(shadow);
    wireEvents(shadow);
  }

  function injectStyle(shadow) {
    var style = document.createElement("style");
    style.textContent = [
      ":host, *{box-sizing:border-box;}",
      ".njnw-bubble{width:60px;height:60px;border-radius:50%;background:" + CFG.accentColor + ";",
      "box-shadow:0 8px 24px rgba(0,0,0,0.25);border:none;cursor:pointer;display:flex;",
      "align-items:center;justify-content:center;transition:transform .15s;}",
      ".njnw-bubble:hover{transform:scale(1.06);}",
      ".njnw-bubble svg{width:26px;height:26px;fill:#fff;}",
      ".njnw-panel{width:320px;max-height:440px;background:#fff;border-radius:14px;",
      "box-shadow:0 20px 50px rgba(0,0,0,0.28);display:none;flex-direction:column;overflow:hidden;",
      "position:absolute;bottom:74px;" + (CFG.position === "bottom-left" ? "left:0;" : "right:0;") + "}",
      ".njnw-panel.open{display:flex;}",
      ".njnw-header{background:" + CFG.accentColor + ";color:#fff;padding:14px 16px;font-weight:700;font-size:14px;",
      "display:flex;align-items:center;justify-content:space-between;}",
      ".njnw-header small{display:block;font-weight:400;font-size:11px;opacity:.85;margin-top:2px;}",
      ".njnw-close{background:transparent;border:none;color:#fff;font-size:18px;cursor:pointer;line-height:1;padding:4px;}",
      ".njnw-body{flex:1;overflow-y:auto;padding:14px;background:#f7f8fa;display:flex;flex-direction:column;gap:8px;min-height:180px;}",
      ".njnw-bubble-msg{max-width:82%;padding:9px 12px;border-radius:12px;font-size:12.8px;line-height:1.45;}",
      ".njnw-bubble-msg.agent{align-self:flex-start;background:#eef1f5;color:#1c2733;border-bottom-left-radius:3px;}",
      ".njnw-bubble-msg.user{align-self:flex-end;background:" + CFG.accentColor + ";color:#fff;border-bottom-right-radius:3px;}",
      ".njnw-quick{display:flex;flex-wrap:wrap;gap:6px;padding:0 14px 10px;background:#f7f8fa;}",
      ".njnw-quick button{background:#fff;border:1.4px solid " + CFG.accentColor + ";color:" + CFG.accentColor + ";",
      "padding:6px 11px;border-radius:16px;font-size:11.5px;font-weight:700;cursor:pointer;}",
      ".njnw-quick button:hover{background:" + CFG.accentColor + ";color:#fff;}",
      ".njnw-inputrow{display:flex;gap:6px;padding:10px;border-top:1px solid #e5e8ec;background:#fff;}",
      ".njnw-inputrow input{flex:1;border:1px solid #e2e6ea;border-radius:16px;padding:8px 12px;font-size:12.5px;outline:none;}",
      ".njnw-inputrow input:focus{border-color:" + CFG.accentColor + ";}",
      ".njnw-inputrow button{background:" + CFG.accentColor + ";color:#fff;border:none;border-radius:16px;padding:0 14px;font-weight:700;font-size:11.5px;cursor:pointer;}",
      ".njnw-footer{text-align:center;font-size:9.5px;color:#9aa4b2;padding:5px 0 8px;background:#fff;}",
      ".njnw-footer a{color:#9aa4b2;}",
    ].join("\n");
    shadow.appendChild(style);
  }

  function buildMarkup() {
    return (
      '<button class="njnw-bubble" id="njnw-toggle" aria-label="Chat with us">' +
      '<svg viewBox="0 0 24 24"><path d="M12 3C6.48 3 2 6.94 2 11.7c0 2.64 1.4 5 3.6 6.57L5 22l4.02-1.79c.94.23 1.94.35 2.98.35 5.52 0 10-3.94 10-8.86S17.52 3 12 3z"/></svg>' +
      '</button>' +
      '<div class="njnw-panel" id="njnw-panel">' +
      '<div class="njnw-header"><div>' + escapeHtml(CFG.businessName) + '<small>Usually replies instantly</small></div>' +
      '<button class="njnw-close" id="njnw-close" aria-label="Close">&times;</button></div>' +
      '<div class="njnw-body" id="njnw-body"></div>' +
      '<div class="njnw-quick" id="njnw-quick"></div>' +
      '<div class="njnw-inputrow"><input id="njnw-input" type="text" placeholder="Type a message..." />' +
      '<button id="njnw-send">Send</button></div>' +
      '<div class="njnw-footer">' + escapeHtml(CFG.poweredByLabel) + '</div>' +
      '</div>'
    );
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // ---------- Conversation engine ----------
  function wireEvents(shadow) {
    var panel = shadow.getElementById("njnw-panel");
    var toggle = shadow.getElementById("njnw-toggle");
    var closeBtn = shadow.getElementById("njnw-close");
    var body = shadow.getElementById("njnw-body");
    var quick = shadow.getElementById("njnw-quick");
    var input = shadow.getElementById("njnw-input");
    var sendBtn = shadow.getElementById("njnw-send");

    var opened = false;
    var lead = {};
    var stage = "idle";

    toggle.addEventListener("click", function () {
      panel.classList.toggle("open");
      if (!opened) {
        opened = true;
        greet();
      }
    });
    closeBtn.addEventListener("click", function () {
      panel.classList.remove("open");
    });

    function addMsg(text, who) {
      var div = document.createElement("div");
      div.className = "njnw-bubble-msg " + who;
      div.textContent = text;
      body.appendChild(div);
      body.scrollTop = body.scrollHeight;
    }

    function setQuick(options) {
      quick.innerHTML = "";
      options.forEach(function (opt) {
        var b = document.createElement("button");
        b.textContent = opt.label;
        b.addEventListener("click", function () {
          addMsg(opt.label, "user");
          quick.innerHTML = "";
          opt.next();
        });
        quick.appendChild(b);
      });
    }

    function greet() {
      addMsg(CFG.greeting, "agent");
      mainMenu();
    }

    function mainMenu() {
      var opts = [];

      if (CFG.showBooking !== false) {
        opts.push({ label: CFG.labels.book, next: startBooking });
      }

      opts.push({ label: CFG.labels.faq, next: startFaq });

      if (CFG.phone) {
        opts.push({
          label: CFG.labels.call,
          next: function () {
            addMsg("Calling " + CFG.phone + "…", "agent");
            window.location.href = "tel:" + CFG.phone.replace(/[^0-9+]/g, "");
          },
        });
      }

      if (CFG.contactUrl) {
        opts.push({
          label: CFG.labels.contact,
          next: function () {
            addMsg("Taking you to our contact page…", "agent");
            window.location.href = CFG.contactUrl;
          },
        });
      }

      setQuick(opts);
    }

    function startBooking() {
      addMsg("Great — what's your name?", "agent");
      stage = "book_name";
    }

    function startFaq() {
      if (!CFG.faqs || !CFG.faqs.length) {
        var fallback = CFG.contactUrl
          ? "I don't have quick answers loaded yet — want to reach us directly instead?"
          : "I don't have quick answers loaded yet — want to book a call instead or leave a message?";
        addMsg(fallback, "agent");
        mainMenu();
        return;
      }
      var opts = CFG.faqs.map(function (f) {
        return {
          label: f.q,
          next: function () {
            addMsg(f.a, "agent");
            setTimeout(function () {
              addMsg("Anything else I can help with?", "agent");
              mainMenu();
            }, 400);
          },
        };
      });
      setQuick(opts);
    }

    function finishLead() {
      addMsg(
        "Thanks, " + (lead.name || "there") + "! We'll reach out to " + (lead.phone || "you") + " shortly to confirm. Anything else?",
        "agent"
      );
      if (CFG.leadWebhook) {
        try {
          fetch(CFG.leadWebhook, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ source: "njnw-widget", business: CFG.businessName, lead: lead, page: window.location.href }),
          }).catch(function () {});
        } catch (e) {}
      }
      stage = "idle";
      mainMenu();
    }

    sendBtn.addEventListener("click", handleSend);
    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter") handleSend();
    });

    function handleSend() {
      var val = input.value.trim();
      if (!val) return;
      addMsg(val, "user");
      input.value = "";

      switch (stage) {
        case "book_name":
          lead.name = val;
          addMsg("Thanks, " + val.split(" ")[0] + ". Best phone number to reach you?", "agent");
          stage = "book_phone";
          break;
        case "book_phone":
          lead.phone = val;
          addMsg("Got it. What's the best day/time for you, roughly?", "agent");
          stage = "book_time";
          break;
        case "book_time":
          lead.time = val;
          finishLead();
          break;
        default:
          var match = findFaqMatch(val);
          if (match) {
            addMsg(match.a, "agent");
            setTimeout(function () {
              addMsg("Anything else I can help with?", "agent");
              mainMenu();
            }, 400);
          } else {
            var noAnswer = CFG.contactUrl
              ? "Sorry, I don't have an answer for that. Please send us a detailed message using the \"" + CFG.labels.contact + "\" option below and we'll get back to you personally."
              : "Sorry, I don't have an answer for that one. Try browsing the questions below, or leave a message and we'll follow up.";
            addMsg(noAnswer, "agent");
            mainMenu();
          }
      }
    }
  }
})();
